import { Fragment } from "react";
import type { ComponentProps, ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";

import { EmptyState } from "@/components/ui/empty-state";
import { Pager } from "@/components/ui/pager";
import { SkeletonRows } from "@/components/ui/skeleton";
import { format } from "@/lib/format";
import { cn } from "@/lib/utils";
import { SystemLabels } from "@/lib/labels/system";

import { SectionCard } from "./section-card";

export function LogListSection<T>({
	title,
	dataComponent,
	emptyIcon,
	emptyTitle,
	isLoading,
	isError,
	data,
	page,
	onPage,
	rowKey,
	renderRow,
}: {
	title: string;
	dataComponent: string;
	emptyIcon: LucideIcon;
	emptyTitle: string;
	isLoading: boolean;
	isError: boolean;
	data: { items: T[]; pages: number } | undefined;
	page: number;
	onPage: (page: number) => void;
	rowKey: (item: T) => string | number;
	renderRow: (item: T) => ReactNode;
}) {
	const items = data?.items ?? [];
	const showPager =
		!isLoading && !isError && data !== undefined && items.length > 0 && data.pages > 1;

	return (
		<SectionCard
			title={title}
			dataComponent={dataComponent}
			footer={showPager ? <Pager page={page} pages={data.pages} onPage={onPage} /> : undefined}
		>
			{isLoading && <SkeletonRows rows={4} />}

			{!isLoading && isError && (
				<p className="text-sm text-destructive">{SystemLabels.loadError}</p>
			)}

			{!isLoading && !isError && data !== undefined && items.length === 0 && (
				<EmptyState icon={emptyIcon} title={emptyTitle} />
			)}

			{!isLoading && !isError && data !== undefined && items.length > 0 && (
				<ul className="flex flex-col divide-y divide-border">
					{items.map((item) => (
						<Fragment key={rowKey(item)}>{renderRow(item)}</Fragment>
					))}
				</ul>
			)}
		</SectionCard>
	);
}

export function LogRow({
	person,
	chip,
	when,
	detail,
	detailMeta,
	detailTone = "muted",
	className,
	...rest
}: ComponentProps<"li"> & {
	person: { id: number; name: string | null } | null;
	chip: ReactNode;
	when: string;
	detail?: string;
	detailMeta?: string | null;
	detailTone?: "muted" | "coral";
}) {
	const name = format.readableName(person?.name);

	return (
		<li className={cn("flex flex-col gap-0.5 py-2.5 first:pt-0 last:pb-0", className)} {...rest}>
			<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
				{person !== null && name !== null && (
					<Link
						to="/pessoa/$id"
						params={{ id: person.id }}
						className="text-sm font-medium text-primary hover:underline"
					>
						{name}
					</Link>
				)}

				{!(person !== null && name !== null) && (
					<span className="text-sm font-medium text-muted-foreground">-</span>
				)}

				{chip}

				<span className="text-xs text-muted-foreground">{when}</span>
			</div>

			{detail !== undefined && (
				<div
					data-slot="detail"
					className={cn(
						"flex flex-col gap-0.5 text-xs",
						detailTone === "coral" ? "text-destructive" : "text-muted-foreground",
					)}
				>
					<span>{detail}</span>
					{detailMeta !== undefined && detailMeta !== null && (
						<span className="text-xs text-muted-foreground">{detailMeta}</span>
					)}
				</div>
			)}
		</li>
	);
}
